import { openNewChatComposer, resetNewChatComposer } from '@/lib/openHomeComposer';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalStore, useAuthStore, useChatInputStore, useModelStore, useCoworkerStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import { useRouter, useGlobalSearchParams } from 'expo-router';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { Image } from 'expo-image';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import ChecklistProgressPill from '@/components/Checklist/ChecklistProgressPill';

const createStyles = (colors) => StyleSheet.create({
  headerText: {
    fontSize: 18,
    fontFamily: 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif',
    fontWeight: 400,
    color: colors.text_color_primary,
  },
  modelSelectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: colors.solid_color_block,
    minWidth: 80,
    maxWidth: 200,
  },
  modelSelectorContainerHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  modelImage: {
    width: 20,
    height: 20,
    borderRadius: 0,
    backgroundColor: '#fff',
  },
  modelName: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text_color_primary,
    maxWidth: 100,
  },
  chevron: {
    marginLeft: 2,
  },
});

const EMPTY_ARRAY = [];

// Model selector component for the home/new chat header (mobile only)
const HomeHeaderModelSelector = React.memo(() => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal } = useModalStore(state => state.actions);
  const setContext = useChatInputStore(state => state.setContext);
  const setSelectedModels = useChatInputStore(state => state.setSelectedModels);
  
  // For new chat, use 'new' as context ID
  const contextId = 'new';
  const selectedModelIds = useChatInputStore(state => 
    state.inputStates?.[contextId]?.selectedModelIds ?? EMPTY_ARRAY
  );
  
  // Get model data from model store
  const models = useModelStore(state => state.models);
  
  // Get selected model objects
  const selectedModels = useMemo(() => {
    if (!selectedModelIds || selectedModelIds.length === 0) return [];
    return selectedModelIds
      .map(id => {
        // Handle both string IDs and model objects
        const modelId = typeof id === 'string' ? id : id?.id;
        return models[modelId];
      })
      .filter(Boolean);
  }, [selectedModelIds, models]);
  
  const isAutoMode = selectedModels.length === 0;
  const displayName = isAutoMode 
    ? t('nav.routes.home.auto') 
    : selectedModels.length === 1 
      ? selectedModels[0]?.name || t('nav.routes.chat.modelFallback')
      : `${selectedModels.length} ${t('nav.routes.home.modelsCount')}`;
  
  const handlePress = () => {
    openModal('ModelSelectorDetailedModal', {
      modelIds: selectedModelIds,
      onChange: (modelIds) => {
        // Ensure we update the correct context (new chat)
        setContext(contextId);
        setSelectedModels(modelIds);
      },
      maxModels: 5,
      noMultiSelectMode: false,
      showDoneButton: true,
      showSelectedModels: true,
    });
  };
  
  return (
    <Pressable 
      style={({ hovered }) => [
        styles.modelSelectorContainer,
        hovered && styles.modelSelectorContainerHovered
      ]}
      onPress={handlePress}
    >
      {selectedModels.length > 0 && selectedModels[0]?.logoPath ? (
        <Image
          source={{ uri: selectedModels[0].logoPath }}
          style={styles.modelImage}
        />
      ) : null}
      <Text style={styles.modelName} numberOfLines={1}>
        {displayName}
      </Text>
      <Icon
        name="chevron-down"
        size={12}
        color={colors.text_color_tertiary}
        style={styles.chevron}
      />
    </Pressable>
  );
});

HomeHeaderModelSelector.displayName = 'HomeHeaderModelSelector';

const HomeRouteNavigationHeaderRight = React.memo(() => {
  const { t } = useTranslation();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const params = useGlobalSearchParams();
  const isTemporaryChat = params?.['temporary-chat'] === 'true';

  const handleLogin = useCallback(() => {
    openModal('LoginModal');
  }, [openModal]);

  const handleSignup = useCallback(() => {
    openModal('GhostSignupModal');
  }, [openModal]);

  const handleNewChat = useCallback(() => {
    openNewChatComposer(router);
  }, [router]);

  const handleCreateNewTemporaryChat = useCallback(() => {
    resetNewChatComposer();
    if (isTemporaryChat) {
      router.replace('/app');
    } else {
      router.replace('/app?temporary-chat=true');
    }
  }, [router, isTemporaryChat]);

  if (isGhost) {
    return (
      <NavigationBarButtonContainer isGapped rightPadding>
        <NavigationBarButton
          text={t('common.signIn')}
          variant="secondary"
          size={isXS ? "small" : "medium"}
          onPress={handleLogin}
        />
        <NavigationBarButton
          text={t('common.signUp')}
          variant="primary"
          size={isXS ? "small" : "medium"}
          onPress={handleSignup}
        />
      </NavigationBarButtonContainer>
    );
  }

  return (
    <NavigationBarButtonContainer rightPadding>
      {hasUser && !isXS && Platform.OS === 'web' && <ChecklistProgressPill />}
      <NavigationBarButton
        icon="ghost"
        solidIcon={isTemporaryChat}
        title={t('nav.routes.home.newTemporaryChat')}
        tooltip={t('nav.routes.home.newTemporaryChatTooltip')}
        tooltipShortcut='⌘ ⇧ I'
        onPress={handleCreateNewTemporaryChat}
      />
    </NavigationBarButtonContainer>
  )
});

HomeRouteNavigationHeaderRight.displayName = 'HomeRouteNavigationHeaderRight';

const HomeHeaderTitle = React.memo(() => {
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const isWorkMode = useCoworkerStore((s) => s.composerMode === 'coworker');
  const params = useGlobalSearchParams();
  const isTemporaryChat = params?.['temporary-chat'] === 'true';
  const isNativeApp = Platform.OS === 'ios' || Platform.OS === 'android';
  // Same centered model pill as a normal new chat. Native temp chats keep it
  // even when the window is wider than the XS breakpoint (iPad / landscape).
  if (!isXS && !(isNativeApp && isTemporaryChat)) return null;

  // Work chooses its own model. Showing Auto/model controls implies a
  // setting the user can change when they cannot, so the header is just Work.
  if (isWorkMode) {
    return <Text style={styles.headerText}>{t('navigation.work')}</Text>;
  }

  return <HomeHeaderModelSelector />;
});
HomeHeaderTitle.displayName = 'HomeHeaderTitle';

function ChatRouteNavigationHeaderLeft() {
  return <DefaultRouteNavigationHeaderLeft />
}

const HomeRouteNavigationOptions = {
  title: 'Gab AI',
  headerTitleAlign: 'center',
  headerTitle: () => <HomeHeaderTitle />,
  headerLeft: () => <ChatRouteNavigationHeaderLeft />,
  headerRight: () => <HomeRouteNavigationHeaderRight />
};

export default HomeRouteNavigationOptions;
